import React, { useState, useEffect, useCallback } from 'react';
import { getVendors, syncInvoicesFromWinTeam } from '../../adminApi';
import { Vendor, InvoiceSyncResult } from '../../adminTypes';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toLocalDateString(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface VendorMultiSelectProps {
  vendors: Vendor[];
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled: boolean;
}

const VendorMultiSelect: React.FC<VendorMultiSelectProps> = ({
  vendors, selected, onChange, disabled,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    return (
      v.vendor_name.toLowerCase().includes(q) ||
      String(v.vendor_number).includes(q)
    );
  });

  const toggle = (num: number) => {
    if (selected.includes(num)) {
      onChange(selected.filter(n => n !== num));
    } else {
      onChange([...selected, num]);
    }
  };

  const removeTag = (num: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(n => n !== num));
  };

  const selectedVendors = vendors.filter(v => selected.includes(v.vendor_number));

  return (
    <div style={{ position: 'relative' }}>
      {/* Tag display / trigger */}
      <div
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          minHeight: '40px',
          border: '1px solid #cbd5e0',
          borderRadius: '6px',
          padding: '6px 10px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#f7fafc' : '#fff',
          userSelect: 'none',
        }}
      >
        {selectedVendors.length === 0 && (
          <span style={{ color: '#a0aec0', fontSize: '14px' }}>
            None — all vendors will be synced
          </span>
        )}
        {selectedVendors.map(v => (
          <span
            key={v.vendor_number}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: '#e8f0ff',
              color: '#00386C',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            {v.vendor_number} — {v.vendor_name}
            {!disabled && (
              <span
                onClick={e => removeTag(v.vendor_number, e)}
                style={{ cursor: 'pointer', fontWeight: 700, marginLeft: '2px' }}
              >
                ×
              </span>
            )}
          </span>
        ))}
        <span style={{ marginLeft: 'auto', color: '#718096', fontSize: '13px' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            zIndex: 100,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            maxHeight: '260px',
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              autoFocus
              placeholder="Search vendors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                border: '1px solid #cbd5e0',
                borderRadius: '4px',
                padding: '6px 10px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: '12px 14px', color: '#718096', fontSize: '13px' }}>
              No vendors found
            </div>
          )}
          {filtered.map(v => (
            <div
              key={v.vendor_number}
              onClick={() => toggle(v.vendor_number)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '9px 14px',
                cursor: 'pointer',
                backgroundColor: selected.includes(v.vendor_number) ? '#f0f4f8' : 'transparent',
                fontSize: '13px',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f4f8')}
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = selected.includes(v.vendor_number)
                  ? '#f0f4f8'
                  : 'transparent')
              }
            >
              <input
                type="checkbox"
                readOnly
                checked={selected.includes(v.vendor_number)}
                style={{ accentColor: '#00386C', width: '15px', height: '15px' }}
              />
              <span style={{ fontWeight: 500, color: '#4a5568', minWidth: '52px' }}>
                #{v.vendor_number}
              </span>
              <span style={{ color: '#1a202c' }}>{v.vendor_name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Overlay to close dropdown */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// ── Result card ───────────────────────────────────────────────────────────────

interface ResultCardProps {
  result: InvoiceSyncResult;
}

const ResultCard: React.FC<ResultCardProps> = ({ result }) => {
  const [showErrors, setShowErrors] = useState(false);

  const stats: Array<{ label: string; value: number; color: string; bg: string }> = [
    { label: 'Found in WinTeam', value: result.total_from_winteam, color: '#1a202c',   bg: '#f0f4f8' },
    { label: 'Imported',         value: result.inserted,           color: '#2d7a2d',   bg: '#e8f5e9' },
    { label: 'Duplicates Skipped', value: result.duplicates,       color: '#6a1b9a',   bg: '#f3e5f5' },
    { label: 'Vendors Excluded', value: result.excluded,           color: '#b8860b',   bg: '#fff8e1' },
    { label: 'Errors',           value: result.errors,             color: result.errors > 0 ? '#c0392b' : '#2d7a2d', bg: result.errors > 0 ? '#fdecea' : '#e8f5e9' },
  ];

  return (
    <div
      style={{
        marginTop: '24px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          backgroundColor: '#f0f4f8',
          padding: '12px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '16px' }}>✅</span>
        <span style={{ fontWeight: 600, color: '#1a202c', fontSize: '15px' }}>
          Sync Complete
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '0',
        }}
      >
        {stats.map((s, i) => (
          <div
            key={s.label}
            style={{
              padding: '20px 16px',
              textAlign: 'center',
              borderRight: i < stats.length - 1 ? '1px solid #e2e8f0' : 'none',
              backgroundColor: s.bg,
            }}
          >
            <div
              style={{ fontSize: '28px', fontWeight: 700, color: s.color, lineHeight: 1 }}
            >
              {s.value}
            </div>
            <div
              style={{ fontSize: '11px', color: '#718096', marginTop: '6px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {result.error_details.length > 0 && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px' }}>
          <button
            onClick={() => setShowErrors(e => !e)}
            style={{
              background: 'none',
              border: 'none',
              color: '#c0392b',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {showErrors ? '▲ Hide' : '▼ Show'} error details ({result.error_details.length})
          </button>
          {showErrors && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {result.error_details.map((e, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: '#fff0ee',
                    border: '1px solid #fbd5d0',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#c0392b',
                    fontFamily: 'monospace',
                  }}
                >
                  Invoice #{e.invoice_number} · Vendor {e.vendor_number} — {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const InvoiceSyncPanel: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(true);

  const [dateFrom, setDateFrom] = useState<string>(toLocalDateString(30));
  const [dateTo,   setDateTo]   = useState<string>(toLocalDateString(0));
  const [excluded, setExcluded] = useState<number[]>([]);

  const [syncing,  setSyncing]  = useState(false);
  const [result,   setResult]   = useState<InvoiceSyncResult | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [winteamEnv, setWinteamEnv] = useState<string>('TRAINING');

  // Load vendors for the exclusion multi-select
  useEffect(() => {
    getVendors()
      .then(vs => setVendors(vs.sort((a, b) => a.vendor_name.localeCompare(b.vendor_name))))
      .catch(() => {})
      .finally(() => setLoadingVendors(false));
  }, []);

  // Load environment for production warning
  useEffect(() => {
    import('../../adminApi').then(({ getSettings }) =>
      getSettings().then(settings => {
        const env = settings.find(s => s.setting_key === 'active_winteam_environment');
        setWinteamEnv(env?.setting_value?.toUpperCase() || 'TRAINING');
      }).catch(() => {})
    );
  }, []);

  const handleSync = useCallback(async () => {
    setError(null);
    setResult(null);

    if (!dateFrom || !dateTo) {
      setError('Please set both a start and end date.');
      return;
    }
    if (dateFrom > dateTo) {
      setError('"From" date must be on or before the "To" date.');
      return;
    }

    setSyncing(true);
    try {
      const res = await syncInvoicesFromWinTeam({
        date_from: dateFrom,
        date_to: dateTo,
        excluded_vendor_numbers: excluded,
      });
      setResult(res);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Sync failed. Check the browser console for details.';
      setError(msg);
    } finally {
      setSyncing(false);
    }
  }, [dateFrom, dateTo, excluded]);

  const isProduction = winteamEnv === 'PRODUCTION';

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 600, color: '#00386C', margin: '0 0 6px' }}>
          Sync Invoices from WinTeam
        </h2>
        <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
          Pulls AP invoices from WinTeam into the portal for the selected date range.
          Each imported invoice receives a tracking ID and is recorded as{' '}
          <strong>POSTED</strong>. Already-imported invoices are skipped automatically.
        </p>
      </div>

      {/* Production warning */}
      {isProduction && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            backgroundColor: '#fff0ee',
            border: '1px solid #fbd5d0',
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '24px',
          }}
        >
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <div style={{ fontSize: '13px', color: '#c0392b', lineHeight: 1.5 }}>
            <strong>You are connected to PRODUCTION.</strong> This sync will pull live
            WinTeam invoices into the production database.
          </div>
        </div>
      )}

      {/* Form card */}
      <div
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '28px',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        {/* Date range */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '24px',
          }}
        >
          <div>
            <label
              style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '6px' }}
            >
              From Date
            </label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={e => { setDateFrom(e.target.value); setResult(null); setError(null); }}
              disabled={syncing}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1a202c',
                backgroundColor: syncing ? '#f7fafc' : '#fff',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
          <div>
            <label
              style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '6px' }}
            >
              To Date
            </label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={e => { setDateTo(e.target.value); setResult(null); setError(null); }}
              disabled={syncing}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1a202c',
                backgroundColor: syncing ? '#f7fafc' : '#fff',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Excluded vendors */}
        <div style={{ marginBottom: '28px' }}>
          <label
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '4px' }}
          >
            Exclude Vendors
          </label>
          <p style={{ fontSize: '12px', color: '#718096', margin: '0 0 8px' }}>
            Invoices from these vendors will be skipped during the sync.
          </p>
          {loadingVendors ? (
            <div style={{ fontSize: '13px', color: '#718096' }}>Loading vendors…</div>
          ) : (
            <VendorMultiSelect
              vendors={vendors}
              selected={excluded}
              onChange={ids => { setExcluded(ids); setResult(null); setError(null); }}
              disabled={syncing}
            />
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div
            style={{
              backgroundColor: '#fdecea',
              border: '1px solid #fbd5d0',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#c0392b',
            }}
          >
            {error}
          </div>
        )}

        {/* Sync button */}
        <button
          onClick={handleSync}
          disabled={syncing || loadingVendors}
          style={{
            padding: '11px 32px',
            backgroundColor: syncing ? '#718096' : '#00386C',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: syncing || loadingVendors ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s',
          }}
        >
          {syncing ? (
            <>
              <span
                style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#ffffff',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              Syncing…
            </>
          ) : (
            <>↓ Sync from WinTeam</>
          )}
        </button>
      </div>

      {/* Results */}
      {result && <ResultCard result={result} />}

      {/* CSS animation for spinner */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default InvoiceSyncPanel;
